import { useEffect, useRef, useState } from "react";
import { NotebookPen, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { ActionButton, SecondaryAction, DictationControl } from "./kit";

// Issue #851 — the saved step's reflection body accepts at most 8,000
// characters (`api/src/routes/reflections.ts:77`'s own `bodySchema`); a
// dictated transcript that would push the box past that is cut to fit
// (D126) rather than silently rejected by the server on Save.
const REFLECT_BODY_MAX_LENGTH = 8000;

// Issue #106: default control height (h-9, 36px) is a mouse-sized target.
// Bump to ~44px on phones where this form is actually used (at the
// ground); hand back to the original density at `sm:` so desktop is
// unchanged. Same constant `PerformanceTracking.jsx` keeps for its own
// controls — duplicated here (rather than imported) because this is the
// one piece of that file's local styling this extraction needs, and a
// shared export for one primitive constant is not worth the indirection.
const TOUCH = "h-11 sm:h-9";

/**
 * "How did that go?" — offered AFTER the innings is saved, never as part of
 * saving it.
 *
 * Issue #759 — extracted verbatim out of `PerformanceTracking.jsx` (issue
 * #216's own component) so `LogMatchSavedStep.jsx` (the new step 3, "the
 * saved step") can reuse it too, exactly as brief-759.md's ruling 2
 * requires: "Keep `ReflectionPrompt`, its POST and `match-reflect-body`,
 * `match-reflect-save` and `match-reflect-skip`." Every testid, the POST
 * itself, and the two guarantees below are unchanged by the move.
 *
 * TWO PROPERTIES THIS COMPONENT EXISTS TO GUARANTEE, both from #216:
 *
 * 1. IT CANNOT BLOCK LOGGING A MATCH. It does not render until
 *    `POST /matches` has already returned 201 — the match is on disk before
 *    this appears, so dismissing it, ignoring it, or closing the tab loses
 *    nothing. A player standing at the ground must not be made to write
 *    prose to save an innings, and the way to guarantee that is for the
 *    prose to live behind a separate request against a separate table, not
 *    in a field on the match form.
 *
 * 2. IT IS NOT A MODAL. Deliberately an inline card rather than a Radix
 *    <Dialog>: a modal takes the focus and blanks the page the player just
 *    came to look at (their new row in the history table), and on a phone it
 *    covers the whole screen for something entirely optional. It also means
 *    the surface can be skipped by simply carrying on — no overlay
 *    intercepting the next tap.
 *
 * Skip is a real button and not just an X, because "Not now" is the honest
 * default and should be as easy to hit as Save.
 *
 * Issue #376(a): `match.body`, when present, is an already-saved
 * reflection's text (passed by `openEdit` in the parent) — this box is now
 * "add or update", not just "add". The save itself still needs no branch:
 * `POST /reflections` for `kind=match` upserts server-side
 * (`upsertMatchReflection`), so re-posting over an existing row edits it in
 * place rather than erroring on the one-per-match constraint.
 *
 * Issue #759, brief-759.md D57 fold-in — `suppressFailureToast` (default
 * `false`, so the shipped inline-on-the-main-page call site and the edit
 * path are BOTH unaffected by this addition). The new saved-step call site
 * (`LogMatchSavedStep.jsx`) passes `true`: D57 ("failures stay on the page;
 * a toast only ever confirms a success") applies to this component's own
 * failure path there too, per the brief's own ruling — a toast riding on
 * top of a screen that otherwise raises none on that path would be exactly
 * the violation #758/#841 already fixed for the scorecard step, reappearing
 * one component over. When suppressed, the failure renders as an inline
 * line instead (`match-reflect-error`) and the box stays open with
 * whatever was typed still in it, so nothing typed is lost.
 *
 * PR #844 review, B1 — `variant` (default `"card"`, the ORIGINAL shape:
 * a shadcn `Card`, the gold-tint background/border, the `NotebookPen` icon,
 * rem-based shadcn `Button`/`Textarea` type). The shipped inline call site
 * (`PerformanceTracking.jsx`, both the flag-off dialog and the edit path)
 * and every existing test of it are UNCHANGED — `variant` is never passed
 * there.
 *
 * `variant="kit"` is new, and used ONLY by `LogMatchSavedStep.jsx`'s own
 * opened state. The review's own findings on the first version of this PR:
 * the opened state was this component's ORIGINAL shadcn shape, untouched —
 * off-palette on every axis token-check samples (`#0F1729` text, `#E1E7EF`
 * borders, none of them a declared round-5 token), a repeated "Match logged
 * vs [opponent]. Want to add a reflection?" heading sitting under the
 * drawn "Add a reflection" card's OWN heading, and the drawn card's own
 * navy-bordered box was REPLACED by a second, visually different
 * gold-tint card rather than continued. `variant="kit"` fixes all three by
 * rendering NO card of its own (the drawn "Add a reflection" card is
 * `LogMatchSavedStep.jsx`'s own wrapper now — this renders only what goes
 * "inside or directly under it," per the review), NO icon, NO repeated
 * heading (the wrapper's own "Add a reflection" already says so), the
 * textarea on kit tokens (`--nw-line` border, `--nw-radius-control`, DM
 * Sans 15px, `--nw-ink`) instead of shadcn's `Textarea`, and the two
 * actions as kit `SecondaryAction` ("Not now") / `ActionButton` ("Save
 * reflection…" — gold, ink label, same one-property override every other
 * gold call site on this screen already needs) instead of shadcn
 * `Button`s — so "Save" is the ONE gold element once the form is open,
 * same as "Write the reflection" was the one gold element before it.
 *
 * Issue #845 (D106, Andreas) — the kit variant's own help line originally
 * kept #216's "Optional — the innings is already saved. A coach you're
 * linked to can read this." verbatim, which repeated the point right under
 * `LogMatchSavedStep.jsx`'s own drawn help line ("Step three of three —
 * and you can still skip it. The match is already saved…"). Ruled: on this
 * step only, the line becomes "A coach you're linked to can read what you
 * write here." — the ONLY new information once the wrapper has already
 * said the match is safe. The `variant="card"` help line below is
 * untouched — the old dialog never sits under that other sentence, so
 * nothing there repeats.
 *
 * Issue #845 (D55) — this variant's own inline failure line
 * (`match-reflect-error`) now carries the alert glyph and the 3px left
 * rule `LogMatchScorecardStep.jsx`'s `ErrorBanner` uses for its own
 * save-failed banner (same background/padding/radius/type), plus the
 * `TriangleAlert` glyph `LogMatchAddGroundPanel.jsx`'s own D55 error
 * already draws beside its left rule — both colour on `--nw-alert` only;
 * the text itself stays ink. `ErrorBanner` is a local, unexported
 * function in that file, so this mirrors its exact values rather than
 * importing it. The `variant="card"` failure line is the pre-#845 shape
 * and is unchanged — D55's finding was scoped to this new path only.
 */
export default function ReflectionPrompt({ match, onDone, suppressFailureToast = false, variant = "card", voiceStatus }) {
  const hadExisting = !!match.body;
  const [body, setBody] = useState(match.body || "");
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const bodyRef = useRef(null);
  const errorRef = useRef(null);
  // Issue #851 — Save waits on both (D117/D157): disabled while recording
  // OR transcribing, never only the latter. `dictationRef` is the mic
  // control's own imperative handle (`discard()`), which "Not now" calls
  // before it drops the reflection (D157) — never a second recorder
  // reference, just the one `DictationControl` mounts.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const dictationRef = useRef(null);

  // Issue #935 (WCAG 2.4.3) — the kit variant is the ONLY thing on the
  // saved step (#871) that opens in place rather than navigating; without
  // this, opening it keyboard-only drops focus to <body>. Mount-only
  // ([]): this component gets a fresh `key={reflectFor.match_id}` per
  // match (`LogMatchSavedStep.jsx`), so one focus per open is exactly one
  // focus per mount. The `variant === "kit"` guard, not a second early
  // return, keeps `bodyRef`/this effect unconditional (hooks can't sit
  // inside the branch below) while never touching the unrelated
  // `variant="card"` shape (#851 keeps this behaviour; that variant is
  // untouched).
  useEffect(() => {
    if (variant === "kit") bodyRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Issue #959 (WCAG 2.4.3) — `disabled={saving}` takes focus off the Save
  // button the moment a save starts, and nothing gave it back when the save
  // failed, so a keyboard-only player landed on <body>. When the kit
  // variant's failure alert renders, focus moves to it (it is
  // `tabIndex={-1}`, so it takes programmatic focus without joining the tab
  // order; Tab from there reaches "Not now" then Save again).
  useEffect(() => {
    if (variant === "kit" && saveFailed) errorRef.current?.focus();
  }, [variant, saveFailed]);

  const save = async () => {
    if (!body.trim()) { onDone(); return; }
    setSaving(true);
    setSaveFailed(false);
    try {
      await api.post("/reflections", { kind: "match", match_id: match.match_id, body });
      toast.success(hadExisting ? "Reflection updated" : "Reflection saved");
      onDone();
    } catch (e) {
      // The match is already saved; a failure here costs the note, not the
      // innings, and the copy says so rather than implying data loss.
      if (suppressFailureToast) {
        setSaveFailed(true);
      } else {
        toast.error("Couldn't save the reflection — your match is logged either way");
      }
    }
    setSaving(false);
  };

  // PR #844 review, B1/token-check — `match-reflect-prompt` names the
  // CLOSED entry button (`Saved.dc.html:124`'s own gold element,
  // brief-759.md ruling 2); it does not move onto this open-state root,
  // so a live capture of the OPENED state correctly has nothing live for
  // token-check's accent rule to compare the drawing's gold
  // `match-reflect-prompt` against, rather than reporting a fake "accent
  // lost" finding for a testid that is legitimately gone once the form
  // the drawing never shows is up. `match-reflect-form` is a new,
  // build-only identifier for this root — not one of the four testids
  // brief-759.md's ruling 2 asks to keep, and not read by any test the
  // shipped `variant="card"` call site needs.
  if (variant === "kit") {
    return (
      <div data-testid="match-reflect-form" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="nw-help">
          A coach you're linked to can read what you write here.
        </div>
        {/* Issue #851 — the box grows a footer strip for the microphone
            (`SavedVoice.dc.html`'s own bordered wrapper), so the border/
            radius that used to sit directly on the `<textarea>` moves to
            this wrapper; the textarea itself is borderless now, matching
            the drawing's inner layout exactly. `bodyRef` is the SAME ref
            #935 already mounts here (`ref={bodyRef}` below, one `ref=`) —
            passed to `DictationControl` as `textareaRef` so it can return
            focus to the box after an insert/undo without a second `ref=`
            prop, which would silently break #935's mount focus (#959). */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-control)",
            overflow: "hidden",
          }}
        >
          <textarea
            ref={bodyRef}
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="How did it actually go? What would you do differently?"
            data-testid="match-reflect-body"
            style={{
              width: "100%",
              minHeight: 88,
              padding: "10px 12px",
              border: "none",
              background: "transparent",
              fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
              fontSize: 15,
              color: "var(--nw-ink)",
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />
          <div style={{ padding: 6, borderTop: "1px solid var(--nw-line-faint, #F1F5F9)" }}>
            <DictationControl
              ref={dictationRef}
              idPrefix="reflect-mic"
              boxLabel="the reflection"
              value={body}
              onChange={setBody}
              textareaRef={bodyRef}
              onRecordingChange={setRecording}
              onTranscribingChange={setTranscribing}
              status={voiceStatus}
              maxLength={REFLECT_BODY_MAX_LENGTH}
            />
          </div>
        </div>
        {saveFailed && (
          <div
            ref={errorRef}
            tabIndex={-1}
            role="alert"
            data-testid="match-reflect-error"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              fontSize: 12,
              lineHeight: 1.5,
              color: "var(--nw-ink)",
              padding: "10px 12px",
              background: "var(--nw-line-faint)",
              borderLeft: "3px solid var(--nw-alert)",
              borderRadius: "var(--nw-radius-control)",
            }}
          >
            <TriangleAlert size={14} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
            <span style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
              Couldn't save the reflection — your match is logged either way. Try again.
            </span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--nw-space-inline)" }}>
          {/* D157 — "Not now" stays enabled while recording or
              transcribing, and drops the audio: it calls the control's
              own `discard()` (stop the recorder and every track, abort
              any request in flight, drop the blob — nothing is sent)
              BEFORE `onDone`, never after. */}
          <SecondaryAction
            onClick={() => {
              dictationRef.current?.discard();
              onDone();
            }}
            data-testid="match-reflect-skip"
          >
            Not now
          </SecondaryAction>
          <ActionButton
            onClick={save}
            disabled={saving || recording || transcribing}
            data-testid="match-reflect-save"
            style={{ width: "auto", padding: "0 20px" }}
          >
            {saving ? "Saving…" : hadExisting ? "Update reflection" : "Save reflection"}
          </ActionButton>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-[#D4A01740] bg-[#D4A0170D]" data-testid="match-reflect-prompt">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <NotebookPen size={16} className="mt-0.5 shrink-0 text-[#8a6a10]" />
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 text-sm">
              {hadExisting
                ? `Your reflection${match.opponent ? ` on the match vs ${match.opponent}` : ""}`
                : `Match logged${match.opponent ? ` vs ${match.opponent}` : ""}. Want to add a reflection?`}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Optional — the innings is already saved. A coach you're linked to can read this.
            </p>
          </div>
        </div>
        <Textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="How did it actually go? What would you do differently?"
          data-testid="match-reflect-body"
        />
        {saveFailed && (
          <p className="text-xs" style={{ color: "#0F172A" }} data-testid="match-reflect-error">
            Couldn't save the reflection — your match is logged either way. Try again.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" className={TOUCH} onClick={onDone} data-testid="match-reflect-skip">
            Not now
          </Button>
          <Button
            className={TOUCH}
            style={{ background: "#0B2E59" }}
            onClick={save}
            disabled={saving}
            data-testid="match-reflect-save"
          >
            {saving ? "Saving…" : hadExisting ? "Update reflection" : "Save reflection"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
